import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form as FormikForm } from "formik";
import { Button } from "react-bootstrap";
import { FaLock, FaKey } from "react-icons/fa";
import { toast } from "react-toastify";
import { authApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import FormField from "../components/forms/FormField";
import { resetPasswordSchema } from "../validation/schemas";
import "./Login.css";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [completed, setCompleted] = useState(false);
  const token = useMemo(() => searchParams.get("token")?.trim() || "", [searchParams]);

  if (!token) {
    return (
      <main className="auth-page">
        <Seo {...PAGE_SEO.resetPassword} />
        <section className="auth-card">
          <div className="auth-form">
            <div className="auth-icon">
              <FaKey />
            </div>
            <h2>Invalid reset link</h2>
            <p className="auth-help-text">
              This password reset link is missing or invalid. Please request a
              new one.
            </p>
            <Link to="/forgot-password" className="auth-btn btn btn-primary">
              Request new link
            </Link>
            <p className="auth-switch">
              <Link to="/login">Back to login</Link>
            </p>
          </div>
        </section>
      </main>
    );
  }

  if (completed) {
    return (
      <main className="auth-page">
        <Seo {...PAGE_SEO.resetPassword} />
        <section className="auth-card">
          <div className="auth-form">
            <div className="auth-icon">
              <FaLock />
            </div>
            <h2>Password updated</h2>
            <p className="auth-help-text">
              Your password has been changed successfully. You can now sign in
              with your new password.
            </p>
            <Button
              type="button"
              className="auth-btn"
              onClick={() => navigate("/login", { replace: true })}
            >
              Go to login
            </Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <Seo {...PAGE_SEO.resetPassword} />
      <section className="auth-card">
        <div className="auth-left">
          <span className="auth-badge">Secure reset</span>
          <h1>Choose a new password</h1>
          <p>
            Enter a strong password you haven&apos;t used on this account
            before.
          </p>
        </div>

        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={resetPasswordSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const res = await authApi.resetPassword({
                token,
                password: values.password,
                confirmPassword: values.confirmPassword,
              });
              toast.success(res.data.message || "Password updated successfully");
              setCompleted(true);
            } catch (error) {
              toast.error(getErrorMessage(error, "Could not reset password"));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <FormikForm className="auth-form" noValidate>
              <div className="auth-icon">
                <FaLock />
              </div>

              <h2>New password</h2>

              <FormField
                name="password"
                type="password"
                label="New password"
                labelIcon={<FaLock />}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />

              <FormField
                name="confirmPassword"
                type="password"
                label="Confirm password"
                labelIcon={<FaLock />}
                placeholder="Re-enter new password"
                autoComplete="new-password"
              />

              <Button type="submit" className="auth-btn" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update password"}
              </Button>

              <p className="auth-switch">
                <Link to="/login">Back to login</Link>
              </p>
            </FormikForm>
          )}
        </Formik>
      </section>
    </main>
  );
};

export default ResetPassword;
